import { useResetPasswordMutation } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { useAuth0 } from '@auth0/auth0-react';
import { Close } from '@mui/icons-material';
import { Alert, Dialog, IconButton, Stack } from '@mui/material';
import * as Sentry from '@sentry/browser';
import { ActionButton, DialogHeading, DialogText } from './styles';
import { useSetAtom } from 'jotai';
import * as state from '@/state';

const USERNAME_PASSWORD_CONNECTION = 'Username-Password-Authentication';

const showSnackbarMessage = (
  message: string,
  severity: 'error' | 'info' | 'success' | 'warning'
) => {
  return (
    <Alert variant="filled" severity={severity}>
      {message && (
        <>
          {message}
          &nbsp;&nbsp;
        </>
      )}
    </Alert>
  );
};

export const ResetPasswordDialog = ({
  open,
  handleClose,
  email,
  connection,
  loginCount,
}: {
  open: boolean;
  handleClose: () => void;
  email: string;
  connection: string | null | undefined;
  loginCount: number | null | undefined;
}) => {
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const currentUser = useAuth0().user;
  const [resetPassword] = useResetPasswordMutation();

  const handleResetPassword = () => {
    handleClose();
    resetPassword({
      variables: {
        input: {
          connection: connection || USERNAME_PASSWORD_CONNECTION,
          clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID ?? '',
          email,
        },
      },
    })
      .then(() => {
        const message =
          loginCount && loginCount > 0
            ? 'Reset password link has been sent successfully'
            : 'Invitation resent successfully';
        setSnackbarMessage({
          shouldShow: true,
          content: showSnackbarMessage(message, 'success'),
        });
      })
      .catch((error) => {
        Sentry.captureMessage(`Failed to reset password ${error.message}`, {
          user: { sub: currentUser },
          extra: {
            connection: USERNAME_PASSWORD_CONNECTION,
            clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
            email: email,
          },
        });
        setSnackbarMessage({
          shouldShow: true,
          content: showSnackbarMessage(error.message, 'error'),
        });
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxWidth: '47.2rem',
          borderRadius: '0.8rem',
        },
      }}
    >
      <Stack gap="0.8rem" padding="2.4rem" paddingBottom="3.2rem">
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <DialogHeading>Reset password</DialogHeading>
          <IconButton disableRipple sx={{ padding: 0 }} onClick={handleClose}>
            <Close sx={{ color: abyssColors.primary[500], fontSize: '2.4rem' }} />
          </IconButton>
        </Stack>
        <DialogText>
          Are you sure you want to reset this user’s password? Clicking "Confirm" will send a
          password reset link to their email.
        </DialogText>
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-end"
        padding="1.6rem 2.4rem"
        borderTop={`1px solid ${abyssColors.primary[100]}`}
      >
        <Stack direction="row" alignItems="center" gap="1.6rem">
          <ActionButton onClick={handleClose}>Cancel</ActionButton>
          <ActionButton contained onClick={handleResetPassword}>
            Send link
          </ActionButton>
        </Stack>
      </Stack>
    </Dialog>
  );
};
